@use '../../../sass/theme';
@use '../../../sass/form-control-mixins';

/**
* theme options
* @prop --jds-theme-input-fs: Default (desktop) input font size
* @prop --jds-theme-input-fs-lg: Mobile (larger) input font size
* @prop --jds-theme-input-fs-sm: Smaller input font size

* @prop --jds-theme-color-input-bg: Input background colour
* @prop --jds-theme-color-input-bg-disabled: Input disabled background colour
* @prop --jds-theme-color-input-bg-readonly: Input readonly background colour

* @prop --jds-theme-color-input-text: Input text colour
* @prop --jds-theme-color-input-text-placeholder: Input placeholder text colour
* @prop --jds-theme-color-input-text-disabled: Input disabled text colour
* @prop --jds-theme-color-input-text-readonly: Input readonly text colour

* @prop --jds-theme-color-input-border: Input border colour
* @prop --jds-theme-color-label-text: Input label text colour
* @prop --jds-theme-color-focus-outline: Input border focus colour
*/

:host {
  display: block;

  .jui-control {
    @include form-control-mixins.jui-control-base;

    > * {
      flex: 1 100%;
    }

    &__label {
      @include form-control-mixins.form-control-label;
      &--disabled {
        @include form-control-mixins.form-label-disabled;
      }

      &--narrower {
        @include form-control-mixins.form-control-label-narrower;
      }
    }

    &__label-text {
      @include form-control-mixins.form-control-label-text;
    }

    &__error {
      @include form-control-mixins.form-control-error;
    }

    &__help {
      @include form-control-mixins.form-control-help;
    }

    &__tooltip {
      @include form-control-mixins.form-control-tooltip;

      &--input-sm {
        @include form-control-mixins.form-control-tooltip-input-sm;
      }

      &--input-lg {
        @include form-control-mixins.form-control-tooltip-input-lg;
      }
    }

    &__tooltip-text {
      @include form-control-mixins.form-control-tooltip-text;

      &--left {
        @include form-control-mixins.form-control-tooltip-text-left;
      }

      &--left-bottom {
        @include form-control-mixins.form-control-tooltip-text-left-bottom;
      }
    }

    &__tooltip-text::before {
      @include form-control-mixins.form-control-tooltip-text-before;
    }

    &__tooltip-text--left::before {
      @include form-control-mixins.form-control-tooltip-text-left-before;
    }

    &__tooltip-text--left-bottom::before {
      @include form-control-mixins.form-control-tooltip-text-left-bottom-before;
    }

    &__tooltip:hover .jui-control__tooltip-text {
      @include form-control-mixins.tooltip-hover;
    }

    &__input {
      @include form-control-mixins.form-control-input-base;
      flex: auto;
      resize: none;

      &:disabled {
        @include form-control-mixins.form-control-disabled;
      }

      &:read-only {
        @include form-control-mixins.form-control-readonly;
      }

      &::placeholder {
        color: var(--jui-input-color-placeholder);
      }

      &--has-focus {
        @include form-control-mixins.form-control-focused;

        & .jui-control__input--invalid {
          border-color: var(--jui-danger-color);
          &:focus-visible {
            outline-color: var(--jui-danger-color);
          }
        }
      }

      &--invalid {
        border-color: var(--jui-danger-color);
        &:focus-visible {
          outline-color: var(--jui-danger-color);
        }
      }
    }

    &:focus-within {
      .jui-control__input {
        @include form-control-mixins.form-control-focused;

        &--invalid {
          border-color: var(--jui-danger-color);
          &:focus-visible {
            outline-color: var(--jui-danger-color);
          }
        }
      }
    }

    &--resize {
      & .jui-control__input {
        resize: vertical;
      }
    }

    // TRANSITIONAL MODIFIER
    &--transitional {
      .jui-control__label {
        top: -26px;
        @include form-control-mixins.smooth-font-transforms;
        transform: translate(9px, 34px) scale(1);
        transition: transform 0.1s ease-in-out;
        user-select: none;
        pointer-events: none;
      }

      .jui-control__input {
        &::placeholder {
          opacity: 0;
        }

        &:focus::placeholder {
          opacity: 1;
          animation-delay: 0.2s;
        }
      }

      .jui-control__label--has-value,
      .jui-control__label--has-focus {
        @include form-control-mixins.smooth-font-transforms;
        transform: translate(-12%, 4px) scale(0.75);
        transition: transform 0.2s ease-out;
      }
    }

    // SIZE MODIFIERS
    &--lg {
      font-size: var(--jui-input-fs-lg);
      line-height: calc(var(--jui-input-fs-lg) * 1.5) !important;
      & .jui-control__icon {
        top: 25px;
      }

      & .jui-control__error {
        font-size: var(--jui-input-fs-lg);
      }

      & .jui-control__input {
        &.jui-control__input--clearable {
          padding-right: 40px;
        }

        &.jui-control__input--has-icon {
          padding-left: 65px;
        }
      }

      &.jui-control--transitional {
        & .jui-control__label {
          transform: translate(9px, 38px) translateZ(0) scale(1);
        }

        & .jui-control__label--has-value,
        & .jui-control__label--has-focus {
          @include form-control-mixins.smooth-font-transforms;
          transform: translate(-12%, 6px) scale(0.75);
          transition: transform 0.2s ease-out;
        }
      }
    }

    &--sm {
      font-size: var(--jui-input-fs-sm);
      line-height: calc(var(--jui-input-fs-sm) * 1.5) !important;
      & .jui-control__icon {
        top: 20px;
      }

      & .jui-control__input {
        font-size: var(--jui-input-fs-sm);
      }

      &.jui-control--transitional {
        & .jui-control__label {
          transform: translate(9px, 31px) translateZ(0) scale(1);
        }

        & .jui-control__label--has-value,
        & .jui-control__label--has-focus {
          @include form-control-mixins.smooth-font-transforms;
          transform: translate(-10%, 4px) scale(0.8);
          transition: transform 0.2s ease-out;
        }
      }
    }
  }
}
